import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';

import type { AggregatedStat } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import { StatBox, StatsCardV1 } from '@feathr/components';

interface ICardProps {
  aggregatedStats: AggregatedStat;
  budget: number;
  conversions: number;
  monetizationValue?: number;
  showBudget?: boolean;
  showCPA?: boolean;
  showCPM?: boolean;
  showMonetizationROI?: boolean;
  spendTooltip?: string;
}

function BudgetCard({
  budget,
  conversions,
  aggregatedStats,
  monetizationValue = 0,
  showBudget = true,
  showCPA = true,
  showCPM = true,
  showMonetizationROI = false,
  spendTooltip,
}: ICardProps): JSX.Element {
  const spend = aggregatedStats.get('spend', 0);
  const views = aggregatedStats.get('num_views', 0);
  const clicks = aggregatedStats.get('num_clicks', 0);
  const emailViews = aggregatedStats.get('num_views_email', 0);
  const emailClicks = aggregatedStats.get('num_clicks_email', 0);
  const cpmViews = views - emailViews;
  const cpcClicks = clicks - emailClicks;

  const spendFormatted = numeral(spend).format('$0,0.00');
  const budgetFormatted = numeral(budget).format('$0,0.00');
  const valueFormatted = numeral(monetizationValue).format('$0,0.00');
  const roiFormatted = numeral(monetizationValue - spend).format('$0,0.00');
  const cpm = numeral(cpmViews > 0 ? spend / (cpmViews / 1000) : 0).format('$0,0.00');
  const cpc = numeral(cpcClicks > 0 ? spend / cpcClicks : 0).format('$0,0.00');
  const cpa = numeral(conversions > 0 ? spend / conversions : 0).format('$0,0.00');

  return (
    <StatsCardV1 title={reportModuleLabels.includeSpendPerformance}>
      <StatBox
        label={'Spend'}
        primary={spendFormatted}
        secondary={showBudget ? `${budgetFormatted} Budget` : ''}
        tooltip={spendTooltip}
      />
      {!!showMonetizationROI && (
        <StatBox
          label={reportModuleLabels.includeMonetizationROI}
          primary={roiFormatted}
          secondary={`Monetization Value: ${valueFormatted}`}
        />
      )}
      {showCPM && (
        <StatBox
          label={'CPM'}
          primary={cpm}
          tooltip={'Cost per Mille: the cost for every thousand views'}
        />
      )}
      <StatBox label={'CPC'} primary={cpc} tooltip={'Cost per Click: the amount spent per click'} />
      {showCPA && (
        <StatBox
          label={'CPA'}
          primary={cpa}
          tooltip={'Cost per Activity: the amount spent per Conversion'}
        />
      )}
    </StatsCardV1>
  );
}

export default BudgetCard;
